import type { ApiClient } from '@hetivo/hetivo-lib-front-utils';
import { toQueryParams, toPaginatedResponse } from '@hetivo/hetivo-lib-front-utils';
import type {
  CreateTerritory,
  PaginatedResponse,
  PaginationParams,
  HetivoApiResponse,
  HetivoPaginatedResponse,
  SortParams,
  Territory,
  TerritoryWithLocationCount,
  UpdateTerritory,
} from '@hetivo/hetivo-lib-types';

import type { TerritorySortableColumn } from '../types/territory.types';

// ─── Params ───────────────────────────────────────────────────────────────────

export interface SearchTerritoriesParams extends PaginationParams {
  name?: string;
  sort?: SortParams<TerritorySortableColumn>;
}

/**
 * The API answered without a territory: it does not exist, or it is outside the caller's scope — the
 * scoped `GET /territories/:id` answers `null` for both (SWOOP-302). Typed so a page can tell "not found"
 * apart from a failed request and handle each its own way.
 */
export class TerritoryNotFoundError extends Error {
  constructor(id: string) {
    super(`getTerritory: territory '${id}' not found (unexpected empty response)`);
    this.name = 'TerritoryNotFoundError';
  }
}

export type CreateTerritoryParams = CreateTerritory;

export type UpdateTerritoryParams = UpdateTerritory;

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createTerritoryApi(client: ApiClient): {
  createTerritory: (params: CreateTerritoryParams) => Promise<Territory>;
  getTerritory: (id: string) => Promise<Territory>;
  searchTerritories: (params: SearchTerritoriesParams) => Promise<PaginatedResponse<TerritoryWithLocationCount>>;
  updateTerritory: (id: string, params: UpdateTerritoryParams) => Promise<Territory>;
} {
  return {
    createTerritory: async (params: CreateTerritoryParams): Promise<Territory> => {
      const response = await client.execute<HetivoApiResponse<Territory>>(() =>
        client.instance.post('/territories', params),
      );
      if (!response.data) {
        throw new Error('createTerritory: unexpected empty response');
      }
      return response.data;
    },

    getTerritory: async (id: string): Promise<Territory> => {
      const response = await client.execute<HetivoApiResponse<Territory>>(() =>
        client.instance.get(`/territories/${id}`),
      );
      if (!response.data) {
        throw new TerritoryNotFoundError(id);
      }
      return response.data;
    },

    searchTerritories: async (
      params: SearchTerritoriesParams,
    ): Promise<PaginatedResponse<TerritoryWithLocationCount>> => {
      const body = toSearchBody(params, 'name');
      const query = toQueryParams(params);
      const response = await client.execute<HetivoApiResponse<HetivoPaginatedResponse<TerritoryWithLocationCount>>>(
        () => client.instance.post('/territories/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('searchTerritories: unexpected empty response');
      }
      return toPaginatedResponse(response.data);
    },

    updateTerritory: async (id: string, params: UpdateTerritoryParams): Promise<Territory> => {
      const response = await client.execute<HetivoApiResponse<Territory>>(() =>
        client.instance.patch(`/territories/${id}`, params),
      );
      if (!response.data) {
        throw new Error('updateTerritory: unexpected empty response');
      }
      return response.data;
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSearchBody<T>(params: T, paramKey: keyof T): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const value = params[paramKey];
  const trimmedValue = typeof value === 'string' ? value.trim() : undefined;
  if (trimmedValue) {
    body[paramKey as string] = trimmedValue;
  }
  return body;
}
